import React from 'react';
import _ from 'underscore';

import {LegacyRunQueryState, RunQueryOutputLegacyData} from 'insights/warehouse/AnalyticsWarehouseTypes';
import cellToItemIds from 'insights/warehouse/cellToItemIds';
import {AsyncStatus} from 'ui-library/hooks/useAsyncState';
import {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';
import Menu from 'ui-library/menus/Menu';
import {OptionContentType} from 'ui-library/menus/MenuConstants';
import Option from 'ui-library/menus/Option';
import Section from 'ui-library/menus/Section';
import SectionHeader from 'ui-library/menus/SectionHeader';

export interface ColumnWithIds {
  name: string;
  uniqueIds: Array<string>;
}

export interface GetExecuteRequestOptionMenuProps {
  columns: Array<ColumnWithIds>;
}

function getTakeActionOnColumnMenu({
  runQueryOutput,
  filterCellContent,
  handleSelectColumn,
  getNoColumnsMessage,
}: TakeActionOnRequestOptionArgs & {runQueryOutput: RunQueryOutputLegacyData}): JSX.Element {
  const columns = runQueryOutput.headers
    .map((header, columnIndex) => ({
      name: header,
      uniqueIds: _.unique(
        runQueryOutput.rows.flatMap(
          (row) => cellToItemIds(row[columnIndex], (cellContent) => filterCellContent(cellContent)) ?? []
        )
      ),
    }))
    .filter((column) => column.uniqueIds.length > 0);

  return (
    <Menu height={Menu.HEIGHTS.MEDIUM} width={Menu.WIDTHS.MEDIUM}>
      <Section>
        <SectionHeader>Column</SectionHeader>
        {columns.length ? (
          columns.map((column, columnIndex) => (
            <Option
              key={`${columnIndex}-${column.name}`}
              contentType={OptionContentType.PLAIN}
              onSelect={() => handleSelectColumn(column)}
              shouldEllipsize={true}
              value={column}
            >
              {column.name}
            </Option>
          ))
        ) : (
          <Option contentType={OptionContentType.PLAIN} isDisabled={true}>
            {getNoColumnsMessage()}
          </Option>
        )}
      </Section>
    </Menu>
  );
}

export interface TakeActionOnRequestOptionArgs {
  getOptionTitle: () => string;
  getNoColumnsMessage: () => string;
  icon: BenchlingIconName;
  filterCellContent: (cellContent: string) => boolean;
  handleSelectColumn: (column: ColumnWithIds) => Promise<void>;
}

export default function buildTakeActionOnColumnOption(
  args: TakeActionOnRequestOptionArgs
): (props: {runQueryState: LegacyRunQueryState}) => JSX.Element {
  return (props) => (
    <Option icon={args.icon} isDisabled={props.runQueryState.status !== AsyncStatus.SUCCESS}>
      {args.getOptionTitle()}
      {props.runQueryState.status === AsyncStatus.SUCCESS &&
        getTakeActionOnColumnMenu({...args, runQueryOutput: props.runQueryState.data})}
    </Option>
  );
}
